export enum BssdkMode {
  GlobalLogin = 0x201,
  UsUnauthenticated = 0x202,
  UsUnauthenticatedHighRisk = 0x203,
  NonUsUnauthenticated = 0x204,
  UsUnder13 = 0x205
}

export type BssdkRegion = 'sg' | 'eu' | 'ttp';

export interface BssdkRuntimeConfig {
  region: BssdkRegion;
  mode: BssdkMode;
  loaderUrl: string;
  isBoe: boolean;
}

export interface BssdkInitOptions {
  aid: number;
  region: BssdkRegion;
  isSDK: false;
  isBoe: boolean;
  mode: BssdkMode;
  initialPathList: string[];
  excludePathList: string[];
}

export const BSSDK_INITIAL_PATH_LIST = ['^/api/.*', '^/oec/selfsvc/v1/feedback/.*'] as const;

export const BSSDK_LOADER_URLS: Record<BssdkRegion, string> = {
  sg: 'https://sf16-website-login.neutral.ttwstatic.com/obj/tiktok_web_login_static/oec/unisec/web/loader/1.0.0.62/sg/index.js',
  eu: 'https://sf16-website-login.neutral.tiktokcdn-eu.com/obj/tiktok_web_login_static_eu/oec/unisec/web/loader/1.0.0.62/eu/index.js',
  ttp: 'https://lf16-cdn-tos.tiktokcdn-us.com/obj/static-tx/oec/unisec/web/loader/2.0.0.32/ttp/index.js'
};

interface ResolveBssdkRuntimeConfigParams {
  idcName?: string;
  hostname?: string;
  search?: string;
}

export const resolveBssdkRuntimeConfig = ({
  idcName = '',
  hostname = '',
  search = ''
}: ResolveBssdkRuntimeConfigParams): BssdkRuntimeConfig | undefined => {
  const normalizedIdc = idcName.toLowerCase();
  const normalizedHostname = hostname.toLowerCase();
  const normalizedSearch = search.toLowerCase();
  const isUs =
    normalizedIdc === 'us-ttp' ||
    normalizedHostname.includes('seller-us.') ||
    normalizedHostname.includes('seller.us.') ||
    normalizedHostname.includes('seller-global-us.');
  const isEu =
    normalizedIdc === 'eu-ttp' ||
    normalizedHostname.includes('seller.eu.') ||
    normalizedHostname.startsWith('seller.eu') ||
    normalizedHostname.includes('seller-global-eu.');
  const isBoe =
    normalizedHostname.includes('boe') ||
    normalizedHostname.endsWith('.net') ||
    normalizedSearch.includes('seller_boe=1');

  const isKnownRowIdc = normalizedIdc === 'sg' || normalizedIdc === 'va';
  const isLocal =
    normalizedHostname === '' ||
    normalizedHostname === 'localhost' ||
    normalizedHostname === '127.0.0.1' ||
    normalizedHostname === '::1';
  const hasKnownIdc = isKnownRowIdc || normalizedIdc === 'eu-ttp' || normalizedIdc === 'us-ttp';
  const hasRecognizedHostname =
    isUs || isEu || isLocal || normalizedHostname.includes('seller') || normalizedHostname.includes('contact');

  if ((normalizedIdc && !hasKnownIdc) || (!normalizedIdc && !hasRecognizedHostname)) {
    return undefined;
  }

  const region: BssdkRegion = isUs ? 'ttp' : isEu ? 'eu' : 'sg';

  return {
    region,
    mode: isUs ? BssdkMode.UsUnauthenticated : BssdkMode.NonUsUnauthenticated,
    loaderUrl: BSSDK_LOADER_URLS[region],
    isBoe
  };
};

export const buildBssdkInitOptions = (
  aid: number,
  runtimeConfig: BssdkRuntimeConfig
): BssdkInitOptions => ({
  aid,
  region: runtimeConfig.region,
  isSDK: false,
  isBoe: runtimeConfig.isBoe,
  mode: runtimeConfig.mode,
  initialPathList: [...BSSDK_INITIAL_PATH_LIST],
  excludePathList: []
});
